import { useParams, Link as RouterLink } from 'react-router-dom'
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { TriangleAlert } from 'lucide-react'
import { getIndustry } from '@/content/site'
import { industryContent } from '@/content/copy'
import { getProduct, productImagesFor } from '@/content/products'
import { colors, layout } from '@/theme'
import { Section } from '@/components/ui/Section'
import { Eyebrow } from '@/components/ui/Eyebrow'
import { Breadcrumbs } from '@/components/ui/Breadcrumbs'
import { SectionHeading } from '@/components/ui/SectionHeading'
import { Media } from '@/components/ui/Media'
import { ProductCard } from '@/components/product/ProductCard'
import { ContactCTA } from '@/components/sections/ContactCTA'
import { Seo } from '@/components/seo/Seo'
import { NotFound } from './NotFound'

export function IndustryDetail() {
  const { slug } = useParams()
  const industry = getIndustry(slug)
  if (!industry) return <NotFound />

  const content = industryContent[industry.slug]
  const gallery = content ? productImagesFor(content.image.sku) : []
  const hero = gallery[content?.image.index ?? 0] ?? gallery[0]
  const recommended = (content?.recommended ?? []).map(getProduct).filter((p) => p !== undefined)

  return (
    <>
      <Seo
        title={industry.name}
        path={`/industries/${industry.slug}`}
        description={content?.blurb ?? `True Forge safety equipment engineered for ${industry.name}.`}
        image={hero?.src}
      />

      {/* Split hero */}
      <Section sx={{ pb: { xs: 8, md: 10 } }}>
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Industries', to: '/industries' }, { label: industry.name }]} />
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Eyebrow>Industries We Serve</Eyebrow>
            <Typography variant="h1" component="h1" sx={{ maxWidth: 640 }}>
              {industry.name}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mt: 3, maxWidth: layout.contentMaxWidth }}>
              {content?.blurb ??
                `True Forge supplies durable, high-performance safety equipment engineered for the demands of ${industry.name.toLowerCase()}.`}
            </Typography>
            <Button variant="contained" component={RouterLink} to="/contact" sx={{ mt: 4 }}>
              Request a Quote
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ borderRadius: `${layout.radius.card}px`, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
              {hero ? (
                <Media src={hero.src} srcSm={hero.srcSm} alt={`${industry.name} — True Forge protection`} width={hero.width} height={hero.height} ratio={4 / 5} />
              ) : (
                <Box sx={{ aspectRatio: '4 / 5', bgcolor: colors.surfaceAlt }} />
              )}
            </Box>
          </Grid>
        </Grid>
      </Section>

      {/* Hazards */}
      {content && content.hazards.length > 0 && (
        <Section sx={{ py: { xs: 7, md: 9 }, bgcolor: colors.surfaceAlt }}>
          <SectionHeading eyebrow="The Challenge" title="What we protect against" />
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {content.hazards.map((h) => (
              <Grid key={h} size={{ xs: 12, sm: 6, md: 3 }}>
                <Stack direction="row" spacing={1.75} alignItems="flex-start">
                  <Box sx={{ color: colors.gold, mt: '2px', flexShrink: 0 }}>
                    <TriangleAlert size={22} />
                  </Box>
                  <Typography variant="h6" component="h3" sx={{ lineHeight: 1.35 }}>
                    {h}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Section>
      )}

      {/* Recommended equipment (tailored) */}
      {recommended.length > 0 && (
        <Section>
          <SectionHeading
            eyebrow="Recommended"
            title="Equipment for this industry"
            description={`A selection from the True Forge range suited to ${industry.name.toLowerCase()} work.`}
          />
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {recommended.map((product) => (
              <Grid key={product!.slug} size={{ xs: 12, sm: 6, md: 3 }}>
                <ProductCard product={product!} />
              </Grid>
            ))}
          </Grid>
        </Section>
      )}

      <ContactCTA />
    </>
  )
}
